import React, { useState, useEffect } from "react";
import { api } from "../lib/api";
import { useData, save } from "../hooks/useData";
import { toast } from "sonner";
import { PageHeader, Card, Button, Field, Input, Select, DataTable } from "../components/shared";
import { useAuth } from "../lib/auth";
import { CompanyDetails } from "../components/CompanyDetails";
import { BulkWipUpload } from "../components/BulkWipUpload";
import { BackupRestore } from "../components/BackupRestore";

const PREFIX_KEYS = [["PO", "Purchase Order"], ["LOT", "Fabric Lot"], ["PROD", "Production Order"], ["CH", "Challan"], ["SO", "Sales Order"]];
const ROLES = ["Admin", "Purchase", "Store", "Production", "Accounts", "Management"];
const STATUS_STYLES = { active: "bg-emerald-50 text-emerald-700", pending: "bg-amber-50 text-amber-700", rejected: "bg-red-50 text-red-600", disabled: "bg-slate-100 text-slate-500" };
const STATUS_LABEL = { active: "Approved / Active", pending: "Pending Approval", rejected: "Rejected", disabled: "Disabled" };
const statusOf = (r) => r.status || (r.active === false ? "disabled" : "active");

export default function SettingsPage() {
  const { user } = useAuth();
  const { data: settings, reload } = useData("/settings");
  const { data: users, reload: reloadUsers } = useData("/users");
  const [prefixes, setPrefixes] = useState({});
  const [allowNeg, setAllowNeg] = useState(false);

  useEffect(() => {
    if (settings) { setPrefixes(settings.prefixes || {}); setAllowNeg(!!settings.allow_negative_stock); }
  }, [settings]);

  const isAdmin = user?.role === "Admin";

  const saveSettings = async () => {
    try { await save("put", "/settings", { prefixes, allow_negative_stock: allowNeg }); toast.success("Settings saved"); reload(); }
    catch {}
  };

  const changeRole = async (u, role) => {
    try { await api.put(`/users/${u.user_id}/role`, { role }); toast.success(`${u.name} → ${role}`); reloadUsers(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed (admin only)"); }
  };
  const setStatus = async (u, status) => {
    try { await api.put(`/users/${u.user_id}/status`, { status }); toast.success(`${u.name} → ${STATUS_LABEL[status]}`); reloadUsers(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const removeUser = async (u) => {
    if (!window.confirm(`Delete ${u.name} (${u.email})? This cannot be undone.`)) return;
    try { await api.delete(`/users/${u.user_id}`); toast.success(`Deleted ${u.name}`); reloadUsers(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const [invite, setInvite] = useState({ email: "", role: "Production" });
  const submitInvite = async (e) => {
    e.preventDefault();
    if (!invite.email.trim()) { toast.error("Enter an email"); return; }
    try { await api.post("/users/invite", invite); toast.success(`Invited ${invite.email} as ${invite.role}`); setInvite({ email: "", role: "Production" }); reloadUsers(); }
    catch (e) { toast.error(e.response?.data?.detail || "Invite failed"); }
  };

  const userCols = [
    { key: "name", header: "Name", render: (r) => <span className="font-medium">{r.name}</span> },
    { key: "email", header: "Email" },
    { key: "role", header: "Role", render: (r) => isAdmin ? (
      <Select className="w-40" value={r.role} onChange={(e) => changeRole(r, e.target.value)} options={ROLES} data-testid={`user-role-${r.user_id}`} />
    ) : r.role },
    { key: "status", header: "Status", render: (r) => { const st = statusOf(r); return <span className={`text-xs px-2 py-0.5 rounded ${STATUS_STYLES[st]}`} data-testid={`user-status-${r.user_id}`}>{STATUS_LABEL[st]}</span>; } },
    ...(isAdmin ? [{ key: "act", header: "Actions", sortable: false, render: (r) => {
      if (r.email === user?.email) return <span className="text-xs text-slate-300">—</span>;
      const st = statusOf(r);
      return (
        <div className="flex flex-wrap items-center gap-1">
          {(st === "pending" || st === "rejected" || st === "disabled") &&
            <Button size="sm" onClick={() => setStatus(r, "active")} data-testid={`user-approve-${r.user_id}`}>Approve</Button>}
          {st === "pending" &&
            <Button size="sm" variant="secondary" onClick={() => setStatus(r, "rejected")} data-testid={`user-reject-${r.user_id}`}>Reject</Button>}
          {st === "active" &&
            <Button size="sm" variant="secondary" onClick={() => setStatus(r, "disabled")} data-testid={`user-disable-${r.user_id}`}>Disable</Button>}
          <Button size="sm" variant="ghost" onClick={() => removeUser(r)} data-testid={`user-delete-${r.user_id}`}>Delete</Button>
        </div>
      );
    } }] : []),
  ];

  return (
    <div>
      <PageHeader title="Settings" subtitle="Company details, WIP upload, numbering, stock policy & user roles" />
      {!isAdmin && <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mb-4">Read-only — Admin access required to change settings.</p>}
      {isAdmin && <CompanyDetails />}
      {isAdmin && <BackupRestore />}
      {isAdmin && <BulkWipUpload />}
      <div className="grid lg:grid-cols-2 gap-4 mb-6">
        <Card className="p-5">
          <p className="text-sm font-semibold text-slate-800 mb-3 font-head">Numbering Prefixes</p>
          <div className="grid grid-cols-2 gap-3">
            {PREFIX_KEYS.map(([k, label]) => (
              <Field key={k} label={label}>
                <Input value={prefixes[k] || ""} placeholder={k} disabled={!isAdmin} onChange={(e) => setPrefixes({ ...prefixes, [k]: e.target.value })} />
              </Field>
            ))}
          </div>
        </Card>
        <Card className="p-5">
          <p className="text-sm font-semibold text-slate-800 mb-3 font-head">Stock Policy</p>
          <Field label="Allow Negative Stock (authorized override)">
            <Select value={allowNeg ? "1" : "0"} disabled={!isAdmin} onChange={(e) => setAllowNeg(e.target.value === "1")}
              options={[{ value: "0", label: "No — block over-issue" }, { value: "1", label: "Yes — allow negative" }]} data-testid="allow-neg" />
          </Field>
          <p className="text-xs text-slate-400 mt-2">When disabled, the system blocks issuing more fabric/WIP/accessories than available.</p>
          {isAdmin && <Button className="mt-4" onClick={saveSettings} data-testid="save-settings-btn">Save Settings</Button>}
        </Card>
      </div>
      <Card className="p-5">
        <p className="text-sm font-semibold text-slate-800 mb-3 font-head">Users & Roles</p>
        {isAdmin && (
          <form onSubmit={submitInvite} className="flex flex-wrap items-end gap-2 mb-4 bg-slate-50 rounded-md p-3">
            <div className="flex-1 min-w-[220px]"><label className="block text-xs text-slate-500 mb-1">Invite by email (Google login)</label>
              <Input type="email" value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} placeholder="person@company.com" data-testid="invite-email" /></div>
            <div><label className="block text-xs text-slate-500 mb-1">Role</label>
              <Select className="w-44" value={invite.role} onChange={(e) => setInvite({ ...invite, role: e.target.value })} options={ROLES} data-testid="invite-role" /></div>
            <Button type="submit" data-testid="invite-btn">Invite User</Button>
          </form>
        )}
        <DataTable testid="users-table" columns={userCols} rows={users || []} dense />
        <p className="text-xs text-slate-400 mt-2">New users who sign in with Google start as <b>Pending Approval</b> and cannot access the app until an admin approves. Invited users are auto-approved. The last active admin can't be removed.</p>
      </Card>
    </div>
  );
}
